/**
 * training-wizard-complete.js
 */

import React from 'react';
import { Typography, IconButton, Collapse } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useHistory, matchPath } from "react-router-dom";
import { parseISO, isAfter } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import { paths } from 'utils/routing';
import { shouldRenderMobileLayout } from 'utils/app-context';
import analytics from 'utils/analytics';
import { UserContext } from 'contexts/user/user-context';
import useRecordStore from 'contexts/record-store';
import { getPatrolSummary } from 'routing/routes/records/record-list/helpers';
import Button from 'components/button';
import Icon from 'components/icon';
import { 
  getSizeTypeAnalyticsLabel, 
  getDurationTypeAnalyticsLabel, 
  getRelTimeTypeAnalyticsLabel 
} from 'components/training/training-utils';
import Alert from '@mui/material/Alert';


const useStyles = makeStyles(theme => ({
  wizardComplete: {
    margin: `${theme.spacing(4)} ${theme.spacing(6)}`,
    backgroundColor: theme.palette.grey.colors[9],
    overflow: 'visible',
    [theme.breakpoints.down('md')]: {
      margin: theme.spacing(3),
    },
    '& > *': {
      display: 'flex',
      alignItems: 'center',
      '& > *': {
        margin: `0 ${theme.spacing(1)}`,
        [theme.breakpoints.down('md')]: {
          margin: `0 ${theme.spacing(0.5)}`
        },
      },
      '& .location-icon': {
        fontSize: theme.typography.sizes[9]
      }
    }
  },

  root: {
    lineHeight: 1,
    margin: shouldRenderMobileLayout()
      ? theme.spacing(2)
      : `${theme.spacing(3)} ${theme.spacing(6)} ${theme.spacing(0)} ${theme.spacing(6)}`,
    '& .MuiAlert-action': {
      padding: shouldRenderMobileLayout()
        ? `0px 0px 0px ${theme.spacing(1)}`
        : `0px ${theme.spacing(1)} 0px ${theme.spacing(2)}`,
    },
    '& .MuiAlert-icon': {
      paddingTop: theme.spacing(1.5),
    },
  },
  title: {
    textTransform: 'uppercase',
    fontWeight: '500',
  },
}));


const CompletionState = {
  PAST_HAS_EXER: 1,
  PAST_HAS_EVENT: 2,
  FUTURE: 3
};


export default function TrainingWizardComplete(props) {
  const { basePath } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const history = useHistory();
  const [completionState, setCompletionState] = React.useState();
  const { t } = useTranslation('general');


  // path params
  const match = matchPath(history.location.pathname, {
    path: basePath + paths.training.base + paths.training.wizardPattern
  });
  const step = match ? parseInt(match.params.step) : null;
  const type = match ? match.params.type : null;
  const id = match ? match.params.id : null;

  // record store
  const recordStore = useRecordStore();
  const record = recordStore.useRecord(Types.Record.getId(type), id).ornull;
  const eventId = type === 'event' ? id : record ? record.get().eventId : 0;


  React.useEffect(() => {
    if (history.location.pathname !== basePath && step === 4) {
      const eventRecord = recordStore.useRecord(Types.Record.EVENT, eventId).ornull;

      if (eventRecord) {
        const event = eventRecord.get();
        const hasExers = event.exercises.length > 0;
        const hasAnswers = event.hasCompletions;

        // exercise-based tracking params
        const memberCount = event.members.length;
        let workModeType = 'N/A'
        let patrolType = 'N/A';
        if (hasExers) {
          const firstExerciseId = event.exercises[0];
          const firstExerciseRecord = recordStore.useRecord(Types.Record.EXERCISE, firstExerciseId);
          const workMode = firstExerciseRecord.get().workMode;
          if (workMode) {
            workModeType = Types.WorkMode.getName(t, workMode);
          }
          if (workMode === Types.WorkMode.PATROL) {
            patrolType = getPatrolSummary(Types.Record.EXERCISE, firstExerciseRecord.patrol.get().patrolTypes, t);
          }
        }

        const completeDescr = hasExers
          ? hasAnswers ? 'WithExersAndCompls' : 'WithExers'
          : 'WithoutExers';

        // The calendar widget makes it easy to manually set the event start time at 1 minute intervals.
        // If the start time isn't set to an even minute then it was probably set to the current time
        // using the widget "today" button or code.
        const startTime = parseISO(event.date);
        const autoSetTime = startTime.getSeconds() !== 0;

        // track
        analytics.trackGA4('event', {
          name: 'event_completewizard',
          params: {
            group_size_type: getSizeTypeAnalyticsLabel(memberCount),
            duration_type: getDurationTypeAnalyticsLabel(event.duration),
            complete_type: completeDescr,
            entry_time_type: getRelTimeTypeAnalyticsLabel(event),
            auto_set_time: autoSetTime,
            workmode_type: workModeType,
            patrol_type: patrolType,
          }
        });

        // completion state
        const isFuture = isAfter(parseISO(event.date), new Date());
        setCompletionState(isFuture
          ? CompletionState.FUTURE
          : hasExers
            ? CompletionState.PAST_HAS_EXER
            : CompletionState.PAST_HAS_EVENT);
      }
    }
  // recordStore is a different object each render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    history.location.pathname, 
    basePath, 
    step,
    eventId,
    //recordStore,
    t,
  ]);


  return (
    <div>
      <Collapse in={Boolean(completionState)}>
        <Alert
          className={classes.root}
          data-testid="training-created-banner"
          action={
            <>
              {/* show an X icon to close this alert banner on mobile */}
              {shouldRenderMobileLayout() &&
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setCompletionState();
                }}
              >
                <Icon type='close' />
              </IconButton>}

              {/* show a Done button to close the entire drawer on desktop */}
              {!shouldRenderMobileLayout() &&
              <Button
                aria-label="close drawer"
                variant="contained"
                startIcon={<Icon type='checkmark' />}
                style={{ boxSizing: 'content-box', padding: '6px 8px' }}
                onClick={() => history.push(basePath)}
              >
                Done
              </Button>}
            </>
          }
          icon={<Icon type='flag' />}
        >
          {/* unique alert message and styles for mobile */}
          {shouldRenderMobileLayout() &&
          <>
            {completionState === CompletionState.PAST_HAS_EXER &&
            <>
              <Typography variant='subtitle2' className={classes.title}>
                Training Created
              </Typography>
              <Typography variant='caption' >
                Your training event and first exercise are recorded. Add more exercises using the button below. 
                {user.role.isHandler && <> Complete any incomplete exercises for your dogs by tapping the exercise rows below.</>}
              </Typography>
            </>}

            {completionState === CompletionState.PAST_HAS_EVENT &&
            <>
              <Typography variant='subtitle2' className={classes.title}>
                Training Created
              </Typography>
              <Typography variant='caption' >
                Your training event is saved. Add exercises using the button below.
              </Typography>
            </>}

            {completionState === CompletionState.FUTURE &&
            <>
              <Typography variant='subtitle2' className={classes.title}>
                Training Scheduled
              </Typography>
              <Typography variant='caption' style={{lineHeight: 1}}>
                Your scheduled event is ready. Add any planned exercises using the button below (optional).
              </Typography>
              </>}
          </>}

          {/* unique alert message and styles for desktop */}
          {!shouldRenderMobileLayout() &&
          <>
            {completionState === CompletionState.PAST_HAS_EXER &&
            <>
              <Typography variant='subtitle1' className={classes.title}>
                Training Created
              </Typography>
              <Typography variant='body2' >
                Your training event and first exercise are recorded. 
                Click <strong>Done</strong> to close this window or add more exercises using the <strong>+&nbsp;Exercise</strong> button on the left. 
                {user.role.isHandler && <> Complete any incomplete exercises for your dogs using the tabs below.</>}
              </Typography>
            </>}

            {completionState === CompletionState.PAST_HAS_EVENT &&
            <>
              <Typography variant='subtitle1' className={classes.title}>
                Training Created
              </Typography>
              <Typography variant='body2' >
                Your training event is saved. Click <strong>Done</strong> to close this window or add exercises using the <strong>+&nbsp;Exercise</strong> button on the left.
              </Typography>
            </>}

            {completionState === CompletionState.FUTURE &&
            <>
              <Typography variant='subtitle1' className={classes.title}>
                Training Scheduled
              </Typography>
              <Typography variant='body2'>
                Your scheduled event is ready. 
                Click <strong>Done</strong> to close this window or add any planned exercises using the <strong>+&nbsp;Exercise</strong> button on the left (optional).
              </Typography>
            </>}
          </>}

        </Alert>
      </Collapse>
    </div>
  );
}